/**
 * What your **subscription plan** includes, what it does not, and what a club adds.
 *
 * **Not `PlanPanel`**, which is the trading journal's plans for one symbol.
 * Two unrelated meanings of the word "plan" live in this app — a trade you have
 * written down, and what you pay for — and the file names have to keep them
 * apart or one of them gets edited by mistake.
 *
 * ## It reads the server's answer and explains it; it decides nothing
 *
 * Every line comes from `/auth/me`. The client cannot work any of it out — the
 * merge across a plan, any number of clubs and per-user grants happens in
 * qv-data — and it must not try, because a second implementation of the merge
 * drifts into a screen that tells somebody they have a feature the server
 * refuses.
 *
 * ## Locked is listed, by name
 *
 * The point of this screen is to answer "what would I get". A list of only what
 * you already hold answers the opposite question. So each axis shows both
 * halves, and the ones you do not hold are named rather than counted.
 *
 * ## Permissive says so, rather than inventing a tier
 *
 * A developer's box and the test suite both report `permissive` — nothing has
 * been decided there. Rendering that as "you are on the top plan" would be a
 * screen asserting a commercial fact that does not exist.
 */

import { useEffect, useState } from "react";

import { accountTitle } from "../api/me";
import {
  type BillingPlans,
  type BillingReturn,
  type PlanIntent,
  heldPlanName,
  joinedGroups,
} from "../lib/billing";
import { useEntitlements } from "../lib/entitlements";
import { signedInEmail } from "../lib/session";
import { BillingSection } from "./BillingSection";

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  width: 460,
  maxWidth: "92vw",
  maxHeight: "70vh",
  overflowY: "auto",
  padding: 14,
  color: "var(--qf-text)",
  font: "13px system-ui, sans-serif",
};

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "10px 0 2px",
};

const NOTE: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontSize: 12,
  lineHeight: 1.5,
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 10,
  padding: "3px 0",
};

/**
 * The features this screen names, in the order somebody would ask about them.
 *
 * A fixed list rather than one derived from the capability set, because a
 * derived list can only show what you **have** — and the job this screen exists
 * for is telling you what you have not. The strings match
 * `app/entitlements/caps.py`; one added there and not here is simply not
 * mentioned, which is a missing line rather than a wrong one.
 */
const FEATURES: ReadonlyArray<{ capability: string; label: string; note: string }> = [
  { capability: "feat:journal", label: "Trading journal", note: "Plans, fills and reviews" },
  { capability: "feat:describe", label: "Describe in English", note: "Screener column authoring" },
  { capability: "feat:describeBuilder", label: "Describe in the builder", note: "Conditions and columns, in words" },
  { capability: "feat:analyst", label: "Analyst summary", note: "The written note on AI Review" },
  { capability: "feat:agent", label: "Agent chat", note: "The QueVee conversation" },
  { capability: "feat:alerts", label: "Alerts", note: "Not built yet" },
  { capability: "data:realtime", label: "Intraday prices", note: "Today's bar as it forms" },
];

const BUDGETS: ReadonlyArray<{ capability: string; label: string }> = [
  { capability: "lim:describeCallsDay", label: "Describe calls" },
  { capability: "lim:describeBuilderCallsDay", label: "Builder describe calls" },
  { capability: "lim:analystCallsDay", label: "Analyst summaries" },
  { capability: "lim:agentCallsDay", label: "Agent messages" },
];

function Included() {
  return <span style={{ color: "var(--qf-positive, #4a4)" }}>included</span>;
}

function Excluded() {
  return <span style={{ color: "var(--qf-text-muted)" }}>not included</span>;
}

function describeSet(held: Set<string> | "*", everything: string): string {
  if (held === "*") return everything;
  if (held.size === 0) return "none";
  return [...held].sort().join(", ");
}

export function PlanScreen({
  billingReturn = null,
  intent = null,
}: {
  billingReturn?: BillingReturn | null;
  /** The card a landing-page button chose: picked and focused. */
  intent?: PlanIntent | null;
}) {
  const { me, ready, can, limitOf, valuesIn } = useEntitlements();
  const [billing, setBilling] = useState<BillingPlans | null>(null);
  // The session's own email, for a server that does not send one yet.
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  useEffect(() => {
    void signedInEmail().then(setSessionEmail);
  }, []);

  if (!ready) return <div style={{ ...PANEL, ...NOTE }}>Loading…</div>;

  if (me === null) {
    return (
      <div style={PANEL}>
        <div style={NOTE}>
          No server is configured for this build, so there is no plan to show and
          nothing is restricted.
        </div>
      </div>
    );
  }

  const email = me.email ?? sessionEmail;
  // **A name, never the account key** — the key is in the hover text.
  const name = me.displayName?.trim() || email?.split("@")[0] || "";
  const groups = joinedGroups(me.orgs);

  return (
    <div style={PANEL}>
      {/* What you may buy, first: it is the question most people open this with.
          Absent when billing is not configured for this deployment. */}
      <BillingSection returned={billingReturn} intent={intent} onPlans={setBilling} />

      <div style={SECTION}>Account</div>
      <div style={ROW} title={accountTitle(me)}>
        <span>{name || "Signed in"}</span>
        <span style={NOTE}>
          {me.permissive
            ? "no plan in force"
            : (heldPlanName(billing, true) ?? (me.plan || "no plan"))}
        </span>
      </div>
      {email && <div style={NOTE}>{email}</div>}

      {me.permissive && (
        <div style={NOTE}>
          Nothing has been restricted on this deployment — everything below is
          available. This is what a development build and a fresh install both
          look like.
        </div>
      )}

      {/* Only the groups somebody joined. The `qf-*` clubs come with the plan
          and are the plan line above said again, as codes. */}
      {groups.length > 0 && (
        <>
          <div style={SECTION}>Groups</div>
          <div style={NOTE}>{groups.join(", ")}</div>
        </>
      )}

      <div style={SECTION}>Data</div>
      <div style={ROW}>
        <span>Markets</span>
        <span style={NOTE}>{describeSet(valuesIn("market"), "all markets")}</span>
      </div>
      <div style={ROW}>
        <span>Universes you can screen</span>
        <span style={NOTE}>{describeSet(valuesIn("group"), "all groups")}</span>
      </div>
      <div style={ROW}>
        <span>Indicator groups</span>
        <span style={NOTE}>{describeSet(valuesIn("ind"), "all indicators")}</span>
      </div>

      <div style={SECTION}>Features</div>
      {FEATURES.map((feature) => (
        <div key={feature.capability} style={ROW}>
          <span>
            {feature.label} <span style={NOTE}>· {feature.note}</span>
          </span>
          {can(feature.capability) ? <Included /> : <Excluded />}
        </div>
      ))}

      <div style={SECTION}>Daily limits</div>
      {BUDGETS.map((budget) => {
        const ceiling = limitOf(budget.capability);
        return (
          <div key={budget.capability} style={ROW}>
            <span>{budget.label}</span>
            <span style={NOTE}>{ceiling === null ? "no limit" : `${ceiling} a day`}</span>
          </div>
        );
      })}
      {/* Honest about what is not here. A usage meter would need an endpoint
          that does not exist, and a fabricated one is worse than an absent one. */}
      <div style={NOTE}>
        These are the ceilings, not what you have used today — the server counts
        per day and refuses past the limit.
      </div>
    </div>
  );
}
